/**
 * Constants for the symmetry/harmony scoring heuristic.
 * All landmark indices reference the 478-point MediaPipe FaceLandmarker mesh.
 * https://developers.google.com/mediapipe/solutions/vision/face_landmarker
 */

/** Pairs of (left, right) landmark indices reflected across the midline. */
export const SYMMETRY_PAIRS: ReadonlyArray<readonly [number, number]> = [
  [33, 263],   // outer eye corners
  [133, 362],  // inner eye corners
  [130, 359],  // upper eye outer
  [159, 386],  // upper eyelids
  [145, 374],  // lower eyelids
  [61, 291],   // mouth corners
  [76, 306],   // mouth outer
  [98, 327],   // nose alae
  [234, 454],  // cheekbones
  [127, 356],  // temples
  [172, 397],  // jaw points
  [136, 365],  // lower jaw
] as const;

/** Two midline points whose mean defines the vertical face axis. */
export const MIDLINE_INDICES = { upper: 1, lower: 168 } as const;

/** Vertical thirds reference points. */
export const THIRDS_INDICES = {
  hairline: 10,
  browLeft: 70,
  browRight: 300,
  noseBase: 2,
  chin: 152,
} as const;

/** Width / height anchors. */
export const FACE_BOUNDS = {
  leftCheek: 234,
  rightCheek: 454,
  top: 10,
  bottom: 152,
} as const;

/**
 * Normalised symmetry slack — fraction of the bounding-box width below which
 * a reflected delta scores ~1.0. 0.10 = up to 10% asymmetry counts as perfect.
 * Real human faces are not mirror-symmetric; tighter values starve the score.
 */
export const K_SYMMETRY = 0.10;

/** Weights for the final score; sum to 1. */
export const WEIGHTS = { sym: 0.5, thirds: 0.3, phi: 0.2 } as const;

/** Golden ratio inverse (1/φ ≈ 0.618) — target width:height. */
export const PHI_INVERSE = 0.618;
export const PHI_TOLERANCE = 0.35;

/** Visibility thresholds (avg landmark confidence per frame). */
export const LIGHTING_OK = 0.75;
export const LIGHTING_REALTIME_WARN = 0.6;
export const LIGHTING_PENALTY_CAP = 2.5;

/** Sampling cadence — exactly 20 frames over 10s, every 500ms. */
export const SAMPLE_INTERVAL_MS = 500;
export const SAMPLE_COUNT = 20;
export const SAMPLE_DEADLINE_SLACK_MS = 150;
